import { useState } from 'react';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Switch } from '@/components/ui-kit/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { Trash } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-media-query';
import { useDeleteLunchMenu, useUpdateLunchMenu } from '../../hooks/use-manage-lunch';
import { LunchMenu, WEEKDAYS } from '../../types/manage-lunch.types';

export interface MenuTableProps {
  menus: LunchMenu[];
  isLoading?: boolean;
}

const LOADING_ROWS = 4;

/** A change waiting for confirmation: deleting a menu, or turning it off. */
type PendingChange = { kind: 'delete' | 'deactivate'; menu: LunchMenu };

export const MenuTable = ({ menus, isLoading = false }: MenuTableProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { mutateAsync: updateLunchMenu, isPending: isUpdating } = useUpdateLunchMenu();
  const { mutateAsync: deleteLunchMenu, isPending: isDeleting } = useDeleteLunchMenu();
  const [pending, setPending] = useState<PendingChange | null>(null);

  // Turning a menu on is immediate; turning it off or deleting it asks first.
  const handleActiveChange = (menu: LunchMenu, checked: boolean) => {
    if (checked) void updateLunchMenu({ itemId: menu.ItemId, input: { IsActive: true } });
    else setPending({ kind: 'deactivate', menu });
  };
  const handleDelete = (menu: LunchMenu) => setPending({ kind: 'delete', menu });

  const handleConfirm = async () => {
    if (!pending) return;
    try {
      if (pending.kind === 'delete') await deleteLunchMenu(pending.menu.ItemId);
      else await updateLunchMenu({ itemId: pending.menu.ItemId, input: { IsActive: false } });
      setPending(null);
    } catch {
      // The error is toasted by the mutation's global handler; the confirmation stays open.
    }
  };

  const isDelete = pending?.kind === 'delete';
  const isConfirming = isDelete ? isDeleting : isUpdating;
  const confirmation = (
    <ConfirmationModal
      open={Boolean(pending)}
      onOpenChange={(open) => !open && setPending(null)}
      title={isDelete ? t('DELETE_LUNCH_MENU') : t('DEACTIVATE_LUNCH_MENU')}
      description={isDelete ? t('MENU_DELETE_WARNING') : t('MENU_DEACTIVATE_WARNING')}
      confirmText={isDelete ? (isDeleting ? t('DELETING') : t('DELETE')) : t('DEACTIVATE')}
      preventAutoClose={isConfirming}
      onConfirm={handleConfirm}
    />
  );

  const menusByDay = WEEKDAYS.map((day) => ({
    day,
    items: menus.filter((menu) => menu.WeekDay === day),
  })).filter((group) => group.items.length > 0);

  if (!isLoading && menusByDay.length === 0) {
    return (
      <Card className="border-none shadow-sm">
        <CardContent className="p-6 text-center text-sm text-muted-foreground md:p-6">
          {t('NO_MENUS_YET')}
        </CardContent>
      </Card>
    );
  }

  // On a phone, per weekday each menu as a card: name with its switch and delete, then the items
  // (the old portal's mobile menu list).
  if (isMobile) {
    if (isLoading) {
      return (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-4">
        {confirmation}
        {menusByDay.map((group) => (
          <div key={group.day} className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">{group.day}</h3>
            {group.items.map((menu) => (
              // The name and items on the left, the switch and delete centered beside both.
              <div
                key={menu.ItemId}
                className="flex items-center gap-2 rounded-lg border bg-card p-4 text-sm"
              >
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <span className="break-words font-semibold">{menu.Name}</span>
                  <span className="break-words text-muted-foreground">
                    {menu.MenuItems.join(', ')}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Switch
                    checked={menu.IsActive}
                    aria-label={t('STATUS')}
                    onCheckedChange={(checked) => handleActiveChange(menu, checked)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(menu)}
                    aria-label={t('DELETE')}
                  >
                    <Trash className="size-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <Card className="rounded-md border-none shadow-sm">
      {confirmation}
      <CardContent className="p-0 md:p-0">
        <Table className="min-w-[36rem]">
          <TableHeader>
            <TableRow>
              <TableHead>{t('DAY')}</TableHead>
              <TableHead>{t('MENU_NAME')}</TableHead>
              <TableHead>{t('MENU_ITEMS')}</TableHead>
              <TableHead>{t('STATUS')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody aria-busy={isLoading}>
            {isLoading &&
              Array.from({ length: LOADING_ROWS }, (_, index) => (
                <TableRow key={`loading-${index}`}>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-28" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-9 rounded-full" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="size-8" />
                  </TableCell>
                </TableRow>
              ))}
            {!isLoading &&
              menusByDay.map((group) =>
                group.items.map((menu, index) => (
                  <TableRow key={menu.ItemId}>
                    {index === 0 && <TableCell rowSpan={group.items.length}>{group.day}</TableCell>}
                    <TableCell>{menu.Name}</TableCell>
                    <TableCell>{menu.MenuItems.join(', ')}</TableCell>
                    <TableCell>
                      <Switch
                        checked={menu.IsActive}
                        aria-label={t('STATUS')}
                        onCheckedChange={(checked) => handleActiveChange(menu, checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(menu)}
                        aria-label={t('DELETE')}
                      >
                        <Trash className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
